import React, { useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, IconButton, InputAdornment, LinearProgress, Paper, Stack,
  TextField, Typography,
} from '@mui/material';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import HeadsetMicIcon from '@mui/icons-material/HeadsetMic';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import axios from 'axios';
import { signOut } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { auth } from './firebaseConfig';
import { apiUrl } from './utils/api';
import Brand from './components/Brand';
import { useLanguage } from '../i18n/LanguageContext';
import LanguageToggle from '../i18n/LanguageToggle';
import { useRole } from '../auth/RoleContext';

const C = { navy: '#0C447C', blue: '#185FA5', ghost: '#E6F1FB', border: '#D7E2EC', text: '#17202A', muted: '#596574' };

// The three kinds of customer. Each becomes the admin of its own tenant (see OnboardingService).
const TYPES = [
  { value: 'OWNER_OPERATOR', icon: <LocalShippingIcon />, title: 'onb.typeOO', desc: 'onb.typeOODesc' },
  { value: 'DISPATCHER', icon: <HeadsetMicIcon />, title: 'onb.typeDispatcher', desc: 'onb.typeDispatcherDesc' },
  { value: 'DRY_VAN', icon: <Inventory2Icon />, title: 'onb.typeDryVan', desc: 'onb.typeDryVanDesc' },
];

// Driver pay % the truck rows start with (the backend applies the same default if left blank).
const DEFAULT_PAY = { OWNER_OPERATOR: '25', DISPATCHER: '25', DRY_VAN: '30' };

const emptyTruck = (type) => ({ number: '', driverName: '', driverPayPct: DEFAULT_PAY[type] || '25' });

const STEPS = 4;

/**
 * Self-onboarding for a brand-new customer, on a phone, with nobody to help:
 *   0. language + kind of business  →  1. business + your name (creates the account and trial)
 *   2. trucks, drivers, driver pay %  →  3. first win: photograph a ticket now.
 * Shown by App.js instead of the app whenever /api/users/me says `needsOnboarding`.
 */
export default function Onboarding() {
  const { t, lang } = useLanguage();
  const { user, refresh } = useRole();
  const navigate = useNavigate();

  const [step, setStep] = useState(0);
  const [type, setType] = useState('');
  const [form, setForm] = useState({ companyName: '', firstName: '', lastName: '' });
  const [trucks, setTrucks] = useState([emptyTruck('')]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  const apiError = (err) => {
    const status = err?.response?.status;
    const body = typeof err?.response?.data === 'string' ? err.response.data : '';
    if (status === 409 && body) return body;
    if (!err?.response) return t('onb.errNetwork');
    return t('onb.errGeneric');
  };

  const pickType = (value) => {
    setType(value);
    setTrucks([emptyTruck(value)]);
    setStep(1);
  };

  // Step 1 → creates tenant, company and user. From here the account exists, so a reload lands in the app.
  const startAccount = async () => {
    const next = {};
    if (!form.companyName.trim()) next.companyName = t('onb.required');
    if (!form.firstName.trim()) next.firstName = t('onb.required');
    setFieldErrors(next);
    if (Object.keys(next).length) return;

    setBusy(true);
    setError('');
    try {
      await axios.post(`${apiUrl}/api/onboarding/start`, {
        businessType: type,
        companyName: form.companyName.trim(),
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        language: lang,
      });
      setStep(2);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const setTruck = (i, key) => (e) => setTrucks((rows) => rows.map((r, j) => (j === i ? { ...r, [key]: e.target.value } : r)));

  // Step 2 → trucks + drivers + pay %. Blank rows are ignored; a truck is required except for dry van
  // (whose first job is a rate confirmation, not a weigh ticket that must match a known truck).
  const saveTrucks = async () => {
    const rows = trucks.filter((r) => r.number.trim());
    const pctBad = rows.some((r) => r.driverPayPct !== '' && (Number(r.driverPayPct) < 0 || Number(r.driverPayPct) > 100 || Number.isNaN(Number(r.driverPayPct))));
    const numbers = rows.map((r) => r.number.trim().toUpperCase());
    if (rows.length === 0 && type !== 'DRY_VAN') { setError(t('onb.needTruck')); return; }
    if (pctBad) { setError(t('onb.badPct')); return; }
    if (new Set(numbers).size !== numbers.length) { setError(t('onb.dupTruck')); return; }

    setBusy(true);
    setError('');
    try {
      await axios.post(`${apiUrl}/api/onboarding/setup`, {
        language: lang,
        trucks: rows.map((r) => ({
          number: r.number.trim(),
          driverName: r.driverName.trim() || null,
          driverPayPct: r.driverPayPct === '' ? null : Number(r.driverPayPct),
        })),
      });
      setStep(3);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  // Leave onboarding: go where the first win is, then re-read the role so the full app appears.
  const finish = (path) => {
    navigate(path);
    refresh();
  };

  const logout = async () => {
    try { await signOut(auth); } catch (e) { /* ignore */ }
    navigate('/login');
  };

  const bigBtn = { height: 52, borderRadius: 2, fontSize: 16, fontWeight: 600, textTransform: 'none', boxShadow: 'none' };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#F6F8FB', pb: 'env(safe-area-inset-bottom)' }}>
      <Box sx={{ bgcolor: C.navy, color: '#fff', px: 2, py: 1.5 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ maxWidth: 520, mx: 'auto' }}>
          <Brand nameColor="#fff" fontSize={16} />
          <LanguageToggle onDark />
        </Stack>
      </Box>
      <LinearProgress variant="determinate" value={((step + 1) / STEPS) * 100} sx={{ height: 4 }} />

      <Box sx={{ maxWidth: 520, mx: 'auto', px: 2, py: 3 }}>
        <Typography sx={{ fontSize: 12, color: C.muted, mb: 0.5 }}>{t('onb.stepOf', { n: step + 1, total: STEPS })}</Typography>

        {step === 0 && (
          <>
            <Typography component="h1" sx={{ fontSize: 24, fontWeight: 700, color: C.text }}>{t('onb.welcome')}</Typography>
            <Typography sx={{ fontSize: 15, color: C.muted, mt: 0.5, mb: 2.5 }}>{t('onb.pickType')}</Typography>
            <Stack spacing={1.5}>
              {TYPES.map((o) => (
                <Paper
                  key={o.value}
                  component="button"
                  type="button"
                  onClick={() => pickType(o.value)}
                  variant="outlined"
                  sx={{
                    p: 2, textAlign: 'left', cursor: 'pointer', borderRadius: 2, borderColor: C.border, bgcolor: '#fff',
                    display: 'flex', alignItems: 'center', gap: 2, width: '100%', font: 'inherit',
                    '&:hover, &:focus-visible': { borderColor: C.blue, bgcolor: C.ghost },
                  }}
                >
                  <Box sx={{ width: 48, height: 48, borderRadius: '50%', bgcolor: C.ghost, color: C.navy, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto' }}>
                    {o.icon}
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: 17, fontWeight: 600, color: C.text }}>{t(o.title)}</Typography>
                    <Typography sx={{ fontSize: 14, color: C.muted }}>{t(o.desc)}</Typography>
                  </Box>
                </Paper>
              ))}
            </Stack>
            <Typography sx={{ fontSize: 13, color: C.muted, mt: 3, textAlign: 'center' }}>{t('onb.trialNote')}</Typography>
          </>
        )}

        {step === 1 && (
          <>
            <Typography component="h1" sx={{ fontSize: 22, fontWeight: 700, color: C.text, mb: 2 }}>{t('onb.aboutYou')}</Typography>
            <Stack spacing={2}>
              <TextField
                label={t(type === 'DRY_VAN' ? 'onb.companyFleet' : 'onb.company')} fullWidth autoFocus size="medium"
                value={form.companyName} onChange={(e) => setForm((f) => ({ ...f, companyName: e.target.value }))}
                error={!!fieldErrors.companyName} helperText={fieldErrors.companyName}
                inputProps={{ maxLength: 120, autoCapitalize: 'words' }}
              />
              <TextField
                label={t('onb.firstName')} fullWidth size="medium" autoComplete="given-name"
                value={form.firstName} onChange={(e) => setForm((f) => ({ ...f, firstName: e.target.value }))}
                error={!!fieldErrors.firstName} helperText={fieldErrors.firstName}
                inputProps={{ maxLength: 80, autoCapitalize: 'words' }}
              />
              <TextField
                label={t('onb.lastName')} fullWidth size="medium" autoComplete="family-name"
                value={form.lastName} onChange={(e) => setForm((f) => ({ ...f, lastName: e.target.value }))}
                inputProps={{ maxLength: 80, autoCapitalize: 'words' }}
              />
              <Typography sx={{ fontSize: 13, color: C.muted }}>{t('onb.signedInAs', { email: user?.email || '' })}</Typography>
            </Stack>
            {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
            <Stack direction="row" spacing={1.5} sx={{ mt: 3 }}>
              <Button variant="outlined" onClick={() => setStep(0)} disabled={busy} sx={{ ...bigBtn, flex: 1 }}>{t('onb.back')}</Button>
              <Button variant="contained" onClick={startAccount} disabled={busy} sx={{ ...bigBtn, flex: 2, bgcolor: C.navy }}>
                {busy ? <CircularProgress size={22} color="inherit" /> : t('onb.startTrial')}
              </Button>
            </Stack>
          </>
        )}

        {step === 2 && (
          <>
            <Typography component="h1" sx={{ fontSize: 22, fontWeight: 700, color: C.text }}>{t('onb.trucksTitle')}</Typography>
            <Typography sx={{ fontSize: 15, color: C.muted, mt: 0.5, mb: 2 }}>{t(type === 'DRY_VAN' ? 'onb.trucksHelpDryVan' : 'onb.trucksHelp')}</Typography>
            <Stack spacing={1.5}>
              {trucks.map((r, i) => (
                <Paper key={i} variant="outlined" sx={{ p: 1.5, borderRadius: 2, borderColor: C.border }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: C.muted }}>{t('onb.truckN', { n: i + 1 })}</Typography>
                    {trucks.length > 1 && (
                      <IconButton aria-label={t('onb.removeTruck')} onClick={() => setTrucks((rows) => rows.filter((_, j) => j !== i))}>
                        <DeleteOutlineIcon />
                      </IconButton>
                    )}
                  </Stack>
                  <Stack spacing={1.5}>
                    <TextField label={t('onb.truckNumber')} size="medium" fullWidth value={r.number} onChange={setTruck(i, 'number')}
                      inputProps={{ maxLength: 30, autoCapitalize: 'characters' }} />
                    <TextField label={t('onb.driverName')} size="medium" fullWidth value={r.driverName} onChange={setTruck(i, 'driverName')}
                      inputProps={{ maxLength: 120, autoCapitalize: 'words' }} helperText={t('onb.driverHelp')} />
                    <TextField label={t('onb.driverPay')} size="medium" fullWidth value={r.driverPayPct} onChange={setTruck(i, 'driverPayPct')}
                      inputProps={{ inputMode: 'decimal', maxLength: 6 }}
                      InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                      helperText={t('onb.driverPayHelp')} />
                  </Stack>
                </Paper>
              ))}
            </Stack>
            {trucks.length < 50 && (
              <Button startIcon={<AddIcon />} onClick={() => setTrucks((rows) => [...rows, emptyTruck(type)])} sx={{ mt: 1.5, textTransform: 'none', fontSize: 15 }}>
                {t('onb.addTruck')}
              </Button>
            )}
            {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
            <Button fullWidth variant="contained" onClick={saveTrucks} disabled={busy} sx={{ ...bigBtn, mt: 3, bgcolor: C.navy }}>
              {busy ? <CircularProgress size={22} color="inherit" /> : t('onb.continue')}
            </Button>
            {type === 'DRY_VAN' && (
              <Button fullWidth onClick={() => setStep(3)} disabled={busy} sx={{ mt: 1, textTransform: 'none' }}>{t('onb.skip')}</Button>
            )}
          </>
        )}

        {step === 3 && (
          <Box sx={{ textAlign: 'center', pt: 2 }}>
            <CheckCircleIcon sx={{ fontSize: 64, color: '#2E7D32' }} />
            <Typography component="h1" sx={{ fontSize: 24, fontWeight: 700, color: C.text, mt: 1 }}>{t('onb.ready')}</Typography>
            <Typography sx={{ fontSize: 15, color: C.muted, mt: 1, mb: 3 }}>{t('onb.trialStarted')}</Typography>
            {type === 'DRY_VAN' ? (
              <Button fullWidth variant="contained" onClick={() => finish('/Masters/Trucks')} sx={{ ...bigBtn, bgcolor: C.navy }}>
                {t('onb.goTrucks')}
              </Button>
            ) : (
              <>
                <Button fullWidth variant="contained" startIcon={<PhotoCameraIcon />} onClick={() => finish('/ScanTicket')} sx={{ ...bigBtn, height: 60, fontSize: 18, bgcolor: C.navy }}>
                  {t('onb.scanNow')}
                </Button>
                <Typography sx={{ fontSize: 13, color: C.muted, mt: 1.5 }}>{t('onb.scanHint')}</Typography>
                <Button fullWidth onClick={() => finish('/Ticket')} sx={{ mt: 2, textTransform: 'none' }}>{t('onb.later')}</Button>
              </>
            )}
          </Box>
        )}

        {step < 2 && (
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Button size="small" startIcon={<LogoutIcon />} onClick={logout} sx={{ textTransform: 'none', color: C.muted }}>{t('nav.logout')}</Button>
          </Box>
        )}
      </Box>
    </Box>
  );
}
